// Sensitive field patterns that must always be redacted before logging.
const SENSITIVE_KEYS = [
  /password/i,
  /secret/i,
  /token/i,
  /authorization/i,
  /cookie/i,
  /session/i,
  /csrf/i,
];

// Default replacement value for sensitive data.
const REDACTED_VALUE = '[REDACTED]';

// Limit how deep we inspect nested objects to avoid infinite recursion.
const MAX_DEPTH = 3;

// Safely stringify values so logError never crashes on circular or unserializable data.
const safeStringify = (value) => {
  try {
    return typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
};

const shouldRedactKey = (key) => SENSITIVE_KEYS.some((pattern) => pattern.test(key));

// Recursively sanitize values, redacting sensitive fields and respecting the max depth limit.
const sanitizeValue = (value, depth = MAX_DEPTH) => {
  if (depth <= 0) {
    return REDACTED_VALUE;
  }

  // Sanitize each element in arrays
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, depth - 1));
  }

  // Sanitize key/value pairs in plain objects.
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      acc[key] = shouldRedactKey(key) ? REDACTED_VALUE : sanitizeValue(val, depth - 1);
      return acc;
    }, {});
  }

  if (typeof value === 'function') {
    return value.name || 'anonymous-function';
  }

  return value;
};

// Normalize any thrown value into a safe, consistent error object for logging.
const normalizeError = (error) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  // Stringify the object for a readable message and sanitize nested fields.
  if (error && typeof error === 'object') {
    return {
      message: safeStringify(error),
      details: sanitizeValue(error),
    };
  }

  // Convert everything to a string so logging never breaks.
  return {
    message: typeof error === 'string' ? error : String(error),
  };
};

/**
 * Creates a safe, structured error log entry.
 * Ensures the thrown value is normalized, sensitive fields are redacted,
 * and the log output has consistent metadata for downstream consumers.
 *
 * @param {unknown} error - Any thrown value (Error, object, string, etc).
 * @param {Record<string, unknown>} [context] - Optional contextual data to include in the log.
 * @returns {{
 *   message: string,
 *   name?: string,
 *   stack?: string,
 *   context?: Record<string, unknown>,
 *   timestamp: string,
 *   severity: string
 * }}
 */
const logError = (error, context = {}) => {
  return {
    ...normalizeError(error),
    context: sanitizeValue(context),
    timestamp: new Date().toISOString(),
    severity: 'error',
  };
};

export default logError;
